import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import useDebouncedValue from 'hooks/useDebouncedValue';

import type { SearchableFormValues } from 'components/SearchableFilter';
import { useOemSegmentsFiltersQuery } from '../useOemSegmentsFiltersQuery';

interface UseOemSegmentsFiltersProps {
  value: string[];
  onSubmit: (data: SearchableFormValues) => void;
}

export default function useOemSegmentsFilters({
  value,
  onSubmit,
}: UseOemSegmentsFiltersProps) {
  const [searchValue, setSearchString, debouncedSearchValue] =
    useDebouncedValue('');

  const {
    data: { managedOemSegments: { collection = [] } = {} } = {},
    loading,
    loadMore,
    loadingMore,
  } = useOemSegmentsFiltersQuery({
    variables: { page: 1, text: debouncedSearchValue },
  });

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
    },
    [setSearchString],
  );

  const items = useMemo(
    () =>
      collection.map((record) => ({
        id: record.id,
        label: record.name,
        value: record.id,
      })),
    [collection],
  );

  const text = useMemo(() => {
    if (value.length === 0) return t`All OEM Segments`;

    if (value.length === 1)
      return (
        items.find((item) => item.value === value[0])?.label || t`OEM Segment`
      );

    return t`OEM Segments (${value.length})`;
  }, [items, value]);

  return {
    items,
    text,
    loading,
    searchValue,
    debouncedSearchValue,
    onSearch,
    mode: 'async' as const,
    value,
    onSubmit,
    onLoadMore: loadMore,
    loadingMore,
  };
}
